import { Injectable, Signal, Type, signal } from '@angular/core';

export interface ModalOpenOptions<TInput extends Record<string, unknown>> {
  badge?: { icon?: string; label: string; };
  component: Type<unknown>;
  id: string;
  inputs?: TInput;
  title?: string;
}

export class ModalRef<TOutput> {
  private dismissed = false;
  private listeners = new Set<(output: TOutput | undefined) => void>();
  private output: TOutput | undefined;

  constructor(
    private onDismissRequest: (output: TOutput | undefined) => void,
  ) {}

  dismiss(output?: TOutput): void {
    this.onDismissRequest(output);
  }

  onDismiss(callback: (output: TOutput | undefined) => void): () => void {
    if (this.dismissed) {
      callback(this.output);
      return () => undefined;
    }
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyDismiss(output?: TOutput): void {
    if (this.dismissed) {
      return;
    }
    this.dismissed = true;
    this.output = output;
    this.listeners.forEach((listener) => listener(output));
    this.listeners.clear();
  }
}

export interface ModalInstance {
  badge?: { icon?: string; label: string; };
  component: Type<unknown>;
  dismissOutput?: Record<string, unknown>;
  id: string;
  inputs?: Record<string, unknown>;
  ref: ModalRef<unknown>;
  title: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modalInstances = signal<ModalInstance[]>([]);

  get(): Signal<ModalInstance[]> {
    return this.modalInstances.asReadonly();
  }

  close<TOutput extends Record<string, unknown> = Record<string, never>>(id: string, output?: TOutput): void {
    this.dismiss(id, output);
  }

  open<TInput extends Record<string, unknown> = Record<string, never>, TOutput extends Record<string, unknown> = Record<string, never>>(options: ModalOpenOptions<TInput>): ModalRef<TOutput> {
    this.dismiss(options.id);
    const ref = new ModalRef<TOutput>((output) => this.dismiss(options.id, output));
    this.modalInstances.update((instances) => [...instances, {
      badge: options.badge,
      component: options.component,
      id: options.id,
      inputs: options.inputs,
      ref: ref as ModalRef<unknown>,
      title: options.title ?? null,
    }]);
    return ref;
  }

  setDismissOutput<TOutput extends Record<string, unknown>>(id: string, output: TOutput): void {
    this.modalInstances.update((instances) => instances.map((instance) => {
      if (instance.id !== id) {
        return instance;
      }
      return {
        ...instance,
        dismissOutput: output,
      };
    }));
  }

  setTitle(id: string, title: string): void {
    this.modalInstances.update((instances) => instances.map((instance) => {
      if (instance.id !== id) {
        return instance;
      }
      return {
        ...instance,
        title,
      };
    }));
  }

  private dismiss(id: string, output?: unknown): void {
    const instances = this.modalInstances();
    const instance = instances.find((item) => item.id === id);
    if (!instance) {
      return;
    }
    this.modalInstances.set(instances.filter((item) => item.id !== id));
    instance.ref.notifyDismiss(output);
  }
}
